import React, { useCallback, useEffect, useId, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CircleDotDashed,
  ExternalLink,
  KeyRound,
  Loader2,
  PlugZap,
  RefreshCw,
  ShieldCheck,
  Unplug,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import {
  deleteLandGateConnection,
  getLandGateConnection,
  LANDGATE_CONNECTION_CHANGED_EVENT,
  LandGateConnectionError,
  putLandGateConnection,
} from '../../services/landgate/landGateConnectionClient';
import type {
  LandGateAuthScheme,
  LandGateConnectionMetadata,
  LandGateConnectionState,
} from '../../services/landgate/landGateConnectionTypes';

export interface LandGateConnectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface LandGateConnectionPanelProps {
  onBusyChange?: (busy: boolean) => void;
  onRequestClose: () => void;
}

type PanelMode = 'status' | 'form' | 'disconnect';

const STATUS_PRESENTATION: Record<LandGateConnectionState, {
  label: string;
  description: string;
  className: string;
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
}> = {
  'not-configured': {
    label: 'Not connected',
    description: 'Add your own LandGate API access to import map layers.',
    className: 'border-border-subtle bg-surface-2 text-text-secondary',
    icon: Unplug,
  },
  'saved-unverified': {
    label: 'Saved · unverified',
    description: 'Stored but not sent to LandGate. Verification starts only after the licensed import contract is enabled.',
    className: 'border-warning/40 bg-warning/10 text-warning',
    icon: CircleDotDashed,
  },
  verified: {
    label: 'Verified',
    description: 'LandGate accepted this connection on its last use.',
    className: 'border-success/40 bg-success/10 text-success',
    icon: CheckCircle2,
  },
  'needs-attention': {
    label: 'Needs attention',
    description: 'Replace the connection before importing more LandGate data.',
    className: 'border-danger/40 bg-danger/10 text-danger',
    icon: AlertTriangle,
  },
};

function formatDate(value: string | null): string {
  if (!value) return 'Not yet';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Not yet';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function statusErrorMessage(error: unknown): string {
  if (error instanceof LandGateConnectionError) return error.message;
  return 'The LandGate connection service could not be reached.';
}

function notifyConnectionChanged(connection: LandGateConnectionMetadata): void {
  window.dispatchEvent(new CustomEvent(LANDGATE_CONNECTION_CHANGED_EVENT, {
    detail: { connection },
  }));
}

/** Stateful connection manager, exported separately for focused settings tests. */
export function LandGateConnectionPanel({
  onBusyChange,
  onRequestClose,
}: LandGateConnectionPanelProps): React.JSX.Element {
  const endpointId = useId();
  const authSchemeId = useId();
  const apiKeyId = useId();
  const attestationId = useId();
  const [connection, setConnection] = useState<LandGateConnectionMetadata | null>(null);
  const [mode, setMode] = useState<PanelMode>('status');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [endpointUrl, setEndpointUrl] = useState('');
  const [authScheme, setAuthScheme] = useState<LandGateAuthScheme>('bearer');
  const [apiKey, setApiKey] = useState('');
  const [licenceSharingAttested, setLicenceSharingAttested] = useState(false);

  const mutating = saving || disconnecting;
  useEffect(() => onBusyChange?.(mutating), [mutating, onBusyChange]);

  const resetForm = useCallback(() => {
    setEndpointUrl('');
    setAuthScheme('bearer');
    setApiKey('');
    setLicenceSharingAttested(false);
  }, []);

  const loadConnection = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const next = await getLandGateConnection(signal);
      setConnection(next);
      setMode(next.state === 'not-configured' ? 'form' : 'status');
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') return;
      setError(statusErrorMessage(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadConnection(controller.signal);
    return () => {
      controller.abort();
      setApiKey('');
    };
  }, [loadConnection]);

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!connection || !licenceSharingAttested || !apiKey.trim()) return;
    setSaving(true);
    setError(null);
    try {
      // Capture the secret for this request only, then remove it from the
      // rendered form immediately after the request has been constructed.
      const oneShotApiKey = apiKey.trim();
      const pending = putLandGateConnection({
        expectedRevision: connection.revision,
        endpointUrl,
        authScheme,
        apiKey: oneShotApiKey,
        licenceSharingAttested: true,
      });
      setApiKey('');
      const next = await pending;
      setConnection(next);
      setMode('status');
      resetForm();
      notifyConnectionChanged(next);
      toast.success('LandGate connection saved');
    } catch (cause) {
      setApiKey('');
      const message = statusErrorMessage(cause);
      setError(message);
      if (cause instanceof LandGateConnectionError
        && cause.code === 'LANDGATE_CONNECTION_CONFLICT') {
        await loadConnection();
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!connection) return;
    setDisconnecting(true);
    setError(null);
    try {
      const next = await deleteLandGateConnection(connection.revision);
      setConnection(next);
      resetForm();
      setMode('form');
      notifyConnectionChanged(next);
      toast.success('LandGate connection disconnected');
    } catch (cause) {
      setError(statusErrorMessage(cause));
      if (cause instanceof LandGateConnectionError
        && cause.code === 'LANDGATE_CONNECTION_CONFLICT') {
        await loadConnection();
      }
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading && !connection) {
    return (
      <div className="flex min-h-56 items-center justify-center gap-2 text-text-secondary" role="status">
        <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
        <span className="type-section">Loading connection status…</span>
      </div>
    );
  }

  if (!connection) {
    return (
      <div className="space-y-4 py-4">
        <div className="border border-danger/40 bg-danger/10 p-3 type-body text-danger" role="alert">
          {error ?? 'Connection status is unavailable.'}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onRequestClose}>Close</Button>
          <Button type="button" variant="mission" onClick={() => void loadConnection()}>
            <RefreshCw aria-hidden /> Retry
          </Button>
        </div>
      </div>
    );
  }

  const presentation = STATUS_PRESENTATION[connection.state];
  const StatusIcon = presentation.icon;

  if (mode === 'form') {
    return (
      <form className="space-y-4" onSubmit={(event) => void handleSave(event)}>
        {connection.state !== 'not-configured' && (
          <div className="border border-warning/40 bg-warning/10 p-3 type-body text-warning">
            Replacing a connection requires the full endpoint and a new API key. Stored credentials are never returned to this browser.
          </div>
        )}

        {error && (
          <div className="border border-danger/40 bg-danger/10 p-3 type-body text-danger" role="alert">
            {error}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_180px]">
          <label className="space-y-1.5" htmlFor={endpointId}>
            <span className="type-section text-text-primary">LandGate endpoint</span>
            <Input
              id={endpointId}
              type="url"
              value={endpointUrl}
              onChange={(event) => setEndpointUrl(event.target.value)}
              placeholder="https://api.landgate.com/…"
              autoComplete="off"
              spellCheck={false}
              required
              disabled={saving}
              data-testid="landgate-endpoint-input"
            />
            <span className="block type-meta text-text-muted">
              Use the complete HTTPS endpoint supplied by LandGate. Query parameters are not accepted.
            </span>
          </label>

          <label className="space-y-1.5" htmlFor={authSchemeId}>
            <span className="type-section text-text-primary">Authentication</span>
            <select
              id={authSchemeId}
              value={authScheme}
              onChange={(event) => setAuthScheme(event.target.value as LandGateAuthScheme)}
              disabled={saving}
              className="h-9 w-full rounded-md border border-border bg-surface-1 px-3 text-sm text-text-primary outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30"
              data-testid="landgate-auth-scheme"
            >
              <option value="bearer">Bearer token</option>
              <option value="x-api-key">X-API-Key</option>
            </select>
          </label>
        </div>

        <label className="space-y-1.5" htmlFor={apiKeyId}>
          <span className="type-section text-text-primary">API key</span>
          <Input
            id={apiKeyId}
            type="password"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            placeholder="Paste your LandGate API key"
            autoComplete="off"
            spellCheck={false}
            required
            disabled={saving}
            data-testid="landgate-api-key-input"
          />
          <span className="block type-meta text-text-muted">
            Sent once to the authenticated server, encrypted there, and never shown again.
          </span>
        </label>

        <label
          htmlFor={attestationId}
          className="flex cursor-pointer items-start gap-3 border border-border-subtle bg-surface-2 p-3"
        >
          <input
            id={attestationId}
            type="checkbox"
            checked={licenceSharingAttested}
            onChange={(event) => setLicenceSharingAttested(event.target.checked)}
            disabled={saving}
            className="mt-0.5 h-4 w-4 accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            data-testid="landgate-licence-attestation"
          />
          <span className="type-body leading-relaxed text-text-secondary">
            I confirm my organisation permits this credential to be stored encrypted in Datacrate. This does not enable or attest data-sharing rights for imports.
          </span>
        </label>

        <div className="flex flex-col-reverse gap-2 border-t border-border-subtle pt-4 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              resetForm();
              if (connection.state === 'not-configured') onRequestClose();
              else setMode('status');
            }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="mission-primary"
            disabled={saving || !endpointUrl.trim() || !apiKey.trim() || !licenceSharingAttested}
            data-testid="landgate-save-connection"
          >
            {saving
              ? <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden />
              : <KeyRound aria-hidden />}
            {saving ? 'Saving…' : 'Save connection'}
          </Button>
        </div>
      </form>
    );
  }

  if (mode === 'disconnect') {
    return (
      <div className="space-y-4">
        <div className="border border-danger/40 bg-danger/10 p-4">
          <div className="flex items-start gap-3">
            <Unplug className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden />
            <div>
              <h3 className="type-section text-text-primary">Disconnect LandGate?</h3>
              <p className="mt-1 type-body leading-relaxed text-text-secondary">
                The stored credential will be deleted. Existing map layers remain available, but new LandGate imports will stop until you connect again.
              </p>
            </div>
          </div>
        </div>
        {error && <div className="type-body text-danger" role="alert">{error}</div>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" disabled={disconnecting} onClick={() => setMode('status')}>
            Keep connection
          </Button>
          <Button
            type="button"
            variant="mission-danger"
            disabled={disconnecting}
            onClick={() => void handleDisconnect()}
            data-testid="landgate-confirm-disconnect"
          >
            {disconnecting
              ? <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden />
              : <Unplug aria-hidden />}
            {disconnecting ? 'Disconnecting…' : 'Disconnect'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="border border-danger/40 bg-danger/10 p-3 type-body text-danger" role="alert">
          {error}
        </div>
      )}

      <div className={`border p-4 ${presentation.className}`} data-testid="landgate-connection-status">
        <div className="flex items-start gap-3">
          <StatusIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="min-w-0">
            <div className="type-section">{presentation.label}</div>
            <p className="mt-1 type-body leading-relaxed">{presentation.description}</p>
          </div>
        </div>
      </div>

      <dl className="grid gap-px overflow-hidden border border-border-subtle bg-border-subtle sm:grid-cols-2">
        <div className="bg-surface-1 p-3">
          <dt className="type-meta text-text-muted">Endpoint host</dt>
          <dd className="mt-1 truncate font-mono text-xs text-text-primary" data-testid="landgate-endpoint-host">
            {connection.endpointHost ?? 'Not available'}
          </dd>
        </div>
        <div className="bg-surface-1 p-3">
          <dt className="type-meta text-text-muted">Authentication</dt>
          <dd className="mt-1 font-mono text-xs text-text-primary">
            {connection.authScheme === 'x-api-key' ? 'X-API-Key' : 'Bearer token'}
          </dd>
        </div>
        <div className="bg-surface-1 p-3">
          <dt className="type-meta text-text-muted">Last verified</dt>
          <dd className="mt-1 font-mono text-xs text-text-primary">{formatDate(connection.lastVerifiedAt)}</dd>
        </div>
        <div className="bg-surface-1 p-3">
          <dt className="type-meta text-text-muted">Credential version</dt>
          <dd className="mt-1 font-mono text-xs text-text-primary">
            {connection.credentialVersion == null ? 'Not available' : `Version ${connection.credentialVersion}`}
          </dd>
        </div>
      </dl>

      <div className="flex items-start gap-2 border-l-2 border-primary bg-primary/5 p-3">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
        <p className="type-body leading-relaxed text-text-secondary">
          Only connection metadata is shown here. Datacrate does not return the endpoint path or API key to the browser.
        </p>
      </div>

      <div className="flex flex-col-reverse gap-2 border-t border-border-subtle pt-4 sm:flex-row sm:items-center sm:justify-between">
        <Button type="button" variant="ghost" className="text-danger hover:text-danger" onClick={() => setMode('disconnect')}>
          <Unplug aria-hidden /> Disconnect
        </Button>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button type="button" variant="outline" onClick={onRequestClose}>Done</Button>
          <Button
            type="button"
            variant="mission"
            onClick={() => {
              resetForm();
              setError(null);
              setMode('form');
            }}
            data-testid="landgate-replace-connection"
          >
            <RefreshCw aria-hidden /> Replace connection
          </Button>
        </div>
      </div>
    </div>
  );
}

export function LandGateConnectionDialog({
  open,
  onOpenChange,
}: LandGateConnectionDialogProps): React.JSX.Element {
  const [busy, setBusy] = useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && busy) return;
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        size="lg"
        className="z-[80] border-border-strong bg-surface-1 p-4 font-mono text-text-primary motion-reduce:animate-none motion-reduce:duration-0 sm:p-5"
        overlayClassName="z-[70] motion-reduce:animate-none motion-reduce:duration-0"
        showCloseButton={!busy}
        data-testid="landgate-connection-dialog"
      >
        <DialogHeader className="border-b border-border-subtle pb-4 pr-8">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center border border-primary/40 bg-primary/10 text-primary">
              <PlugZap className="h-4 w-4" aria-hidden />
            </div>
            <div className="min-w-0">
              <DialogTitle className="type-display text-text-primary">Your LandGate connection</DialogTitle>
              <DialogDescription className="mt-1 type-body leading-relaxed text-text-secondary">
                Prepare your organisation’s LandGate credential securely. Data import remains unavailable until LandGate’s private API contract and sharing terms are validated.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {open && (
          <LandGateConnectionPanel
            onBusyChange={setBusy}
            onRequestClose={() => handleOpenChange(false)}
          />
        )}

        <DialogFooter className="items-center sm:justify-between">
          <a
            href="https://www.landgate.com/capital-markets/api-data-exports"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 type-meta text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            Request LandGate API access <ExternalLink className="h-3 w-3" aria-hidden />
          </a>
          <span className="type-meta text-text-muted">Connection vault ready · provider data import intentionally unavailable.</span>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
